import { addSpacesToTitleCase } from "_shared/_utilities/add-spaces-to-title-case";
import { enumKeys, enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import { getWorkAgreementName, isPermissionsRow, isTemplateRow } from "permissions/_views/tables/_utilities/utils";
import Permission from "permissions/permission";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { AccessLevel } from "permissions/resources/types";
import { WorkAgreementField } from "permissions/resources/work/types";
import { WorkAgreementAccess } from "permissions/resources/work/work-permissions-resources";
import { GroupedCellConfig, CardType, MetaControlType } from "permissions/_views/types/permission-types";
import { JSX } from "react";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";
import { WorkAgreementError } from "work/work-agreements/_store/work-agreement-redux-slice";

export default function WorkAgreementGroupCell({
  row,
  loadingWorkAgreementIds,
  allWorkAgreementErrors,
  barebonesWorkAgreements,
  renderGroupedPermission,
}: {
  row: PermissionsByGrantee | null;
  loadingWorkAgreementIds: Guid[];
  allWorkAgreementErrors: WorkAgreementError[];
  barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[];
  renderGroupedPermission: (row: PermissionsByGrantee, config: GroupedCellConfig) => JSX.Element;
}): JSX.Element | null {
  if (!row?.recentPermissionsPreview) return null;
  // Determine dynamic title (single work agreement -> show its name)
  const workAgreementResources = row.recentPermissionsPreview
    .filter((p): p is Permission & { resource: WorkAgreementAccess } => p.resource instanceof WorkAgreementAccess)
    .map((p) => p.resource);

  const uniqueWorkAgreementIds = Array.from(new Set(workAgreementResources.map((r) => r.workAgreementId?.value)));
  const title =
    uniqueWorkAgreementIds.length === 1 && workAgreementResources[0]
      ? getWorkAgreementName(
          workAgreementResources[0],
          loadingWorkAgreementIds,
          allWorkAgreementErrors,
          barebonesWorkAgreements
        )
      : undefined;

  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(WorkAgreementField).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    candidatesForKey: (row, waKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) => {
          if (!(permission.resource instanceof WorkAgreementAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = permission.resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      if (isTemplateRow(row)) {
        return row.resources.filter((resource) => {
          if (!(resource instanceof WorkAgreementAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      return [];
    },
    labelForKey: (waKey) => addSpacesToTitleCase(waKey),
    controlMetaForResource: (resource, waKey) => {
      const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
      const fields =
        ((resource as WorkAgreementAccess).fields as Partial<Record<WorkAgreementField, AccessLevel>>) ?? {};
      const current = fields[fieldKey];
      const opts = enumKeys(AccessLevel);
      const value = current ? enumKeyOf(AccessLevel, current) : opts[0];
      return { type: MetaControlType.AccessLevel, label: "Access Level", options: opts, value };
    },
    title,
    colSpan: 2,
    rowSpan: 2,
    canEdit: true,
  });
}
